import { EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { Button, Divider, Flex, Table } from 'antd'
import { TableColumnsType } from 'antd/lib'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useClassifierServiceGetCommonClassifierElementEndpointKey,
  useCounterpartServiceDeleteOtherInstitutionEndpoint,
  useCounterpartServiceGetOtherInstitutionListEndpoint,
  useCounterpartServiceGetOtherInstitutionListEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  ClassifierCategory,
  ClassifierService,
  ClassifierType
} from 'src/api/requests'
import {
  DeleteConfirm,
  message,
  PageBreadcrumbs,
  Paragraph,
  SearchBar,
  TableActionButtons,
  ClassifierInfiniteSelect
} from 'src/components'
import { useClassifierId, useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, resetPagination } from 'src/utils'

const DEFAULT_PER_PAGE = 5

export const OtherInstitutionsPage = () => {
  const { t } = useLocales()
  const pagination = useTablePagination()
  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [selectedType, setSelectedType] = useState('')

  const queryParams = {
    pageSize: DEFAULT_PER_PAGE,
    pageNumber: Number(pagination.current),
    ...(!!search && {
      searchTerm: search
    }),
    ...(!!selectedType && {
      subtype: selectedType
    })
  }

  const institutionTypeId = useClassifierId(
    ClassifierType.INSTITUTION_TYPE,
    ClassifierCategory.INSTITUTION
  )

  const { data, isLoading } =
    useCounterpartServiceGetOtherInstitutionListEndpoint(queryParams)

  const institutionTypes = useQueries({
    queries:
      data?.data?.map((item) => ({
        queryKey: [
          useClassifierServiceGetCommonClassifierElementEndpointKey,
          String(institutionTypeId),
          String(item?.subtype)
        ],
        queryFn: async () => {
          const response =
            await ClassifierService.getCommonClassifierElementEndpoint(
              String(institutionTypeId),
              String(item?.subtype)
            )

          return response
        },
        enabled: !!institutionTypeId
      })) || []
  })

  const { mutate } = useCounterpartServiceDeleteOtherInstitutionEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useCounterpartServiceGetOtherInstitutionListEndpointKey]
      })
      message.success(t('other_institutions.delete_institution_success'))
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const getActionItems = useMemo(
    () => (id: string) => {
      return [
        {
          key: 'edit',
          label: t('common.edit'),
          icon: <EditOutlined />,
          onClick: () => {
            navigate(paths.institutions.details(String(id)))
          }
        },
        {
          key: 'delete',
          label: (
            <DeleteConfirm
              title={t('other_institutions.delete_institution')}
              onConfirm={() => mutate({ id })}
            />
          ),
          danger: true
        }
      ]
    },
    [t]
  )

  const columns = useMemo(
    (): TableColumnsType => [
      {
        key: 'institution',
        title: t('common.institution'),
        dataIndex: 'name',
        width: 400
      },
      {
        key: 'address',
        title: t('other_institutions.address'),
        render: ({ address }) => (
          <Paragraph weight={400}>
            {address?.street}, {address?.city}, {address?.region}
          </Paragraph>
        )
      },
      {
        key: 'contact data',
        title: t('other_institutions.contact_data'),
        width: 400,
        render: ({ contactInfo }) => (
          <div>
            <Paragraph weight={400}>{contactInfo?.phone}</Paragraph>
            <Paragraph weight={400}>{contactInfo?.email}</Paragraph>
          </div>
        )
      },
      {
        key: 'type',
        title: t('organizational_structure.institution_type'),
        render: ({ type }) => {
          return type ? t(`common.${type}`) : '-'
        }
      },

      {
        key: 'actions',
        title: t('common.action'),
        align: 'center',
        render: ({ id }) => (
          <TableActionButtons justify="center" items={getActionItems(id)} />
        )
      }
    ],
    [t, getActionItems, institutionTypes]
  )

  const onSearch = (value: string) => {
    setSearch(value)
    resetPagination(pagination)
  }

  const onTypeSelect = (value: string) => {
    setSelectedType(value)
    resetPagination(pagination)
  }

  return (
    <PageBreadcrumbs
      title={t('menu.other_institutions')}
      items={[{ title: t('menu.other_institutions') }]}
    >
      <Flex justify="space-between">
        <Flex gap={16}>
          <SearchBar onSearch={(val) => onSearch(val)} maxWidth={260} />

          <ClassifierInfiniteSelect
            id={institutionTypeId}
            onSelect={(val) => onTypeSelect(val)}
            placeholder={t('organizational_structure.type')}
          />
        </Flex>
        <Button
          icon={<PlusOutlined />}
          type="primary"
          onClick={() => navigate(paths.institutions.add)}
        >
          {t('other_institutions.add_institution')}
        </Button>
      </Flex>

      <Divider />

      <Table
        rowKey="id"
        columns={columns}
        loading={isLoading}
        dataSource={data?.data}
        scroll={{ x: 'max-content' }}
        pagination={{
          ...pagination,
          hideOnSinglePage: true,
          pageSize: DEFAULT_PER_PAGE,
          total: data?.totalCount
        }}
      />
    </PageBreadcrumbs>
  )
}
